/**
 * SyncRetryService
 * Handles exponential backoff retry logic for failed sync records
 * Retry intervals: 1 minute, 5 minutes, 15 minutes
 */

import { logger } from './logger'

// Retry intervals in milliseconds
const RETRY_INTERVALS = [
    60 * 1000, // 1 minute
    5 * 60 * 1000, // 5 minutes
    15 * 60 * 1000 // 15 minutes, and capped here for all subsequent retries
]

interface RetryQueueItem {
    recordId: string
    retryCount: number
    nextRetryAt: number // timestamp in milliseconds
}

class SyncRetryService {
    private retryQueue: Map<string, RetryQueueItem> = new Map()
    private retryTimer: number | null = null

    /**
     * Schedule a retry for a failed record
     * @param recordId - The ID of the failed record
     * @param currentRetryCount - Current retry count for the record
     */
    scheduleRetry(recordId: string, currentRetryCount: number): void {
        // Cap interval at the last defined value — never abandon the record
        const retryInterval =
            RETRY_INTERVALS[currentRetryCount] ?? RETRY_INTERVALS[RETRY_INTERVALS.length - 1]
        const nextRetryAt = Date.now() + retryInterval

        logger.info(
            `[SyncRetry] Scheduling retry for record ${recordId} in ${retryInterval / 1000}s (attempt ${currentRetryCount + 1})`
        )

        // Add to retry queue
        this.retryQueue.set(recordId, {
            recordId,
            retryCount: currentRetryCount,
            nextRetryAt
        })

        // Start retry timer if not already running
        this.startRetryTimer()
    }

    /**
     * Remove a record from the retry queue
     * @param recordId - The ID of the record to remove
     */
    removeFromQueue(recordId: string): void {
        this.retryQueue.delete(recordId)
        logger.info(`[SyncRetry] Removed record ${recordId} from retry queue`)

        // Stop timer if queue is empty
        if (this.retryQueue.size === 0 && this.retryTimer !== null) {
            clearTimeout(this.retryTimer)
            this.retryTimer = null
        }
    }

    /**
     * Get all records due for retry
     * @returns Array of record IDs that should be retried
     */
    getDueRetries(): string[] {
        const now = Date.now()
        const dueRecords: string[] = []

        for (const [recordId, item] of this.retryQueue) {
            if (item.nextRetryAt <= now) {
                dueRecords.push(recordId)
            }
        }

        return dueRecords
    }

    /**
     * Start the retry timer to process retries
     */
    private startRetryTimer(): void {
        // Clear existing timer
        if (this.retryTimer !== null) {
            clearTimeout(this.retryTimer)
        }

        // Find the next retry time
        let nextRetryTime = Infinity
        for (const item of this.retryQueue.values()) {
            if (item.nextRetryAt < nextRetryTime) {
                nextRetryTime = item.nextRetryAt
            }
        }

        // If we have a next retry time, schedule it
        if (nextRetryTime !== Infinity) {
            const delay = Math.max(0, nextRetryTime - Date.now())
            this.retryTimer = window.setTimeout(() => {
                this.processRetries()
            }, delay)

            logger.info(`[SyncRetry] Next retry scheduled in ${delay / 1000}s`)
        }
    }

    /**
     * Process all due retries
     */
    private async processRetries(): Promise<void> {
        const dueRecords = this.getDueRetries()

        if (dueRecords.length === 0) {
            logger.info('[SyncRetry] No retries due')
            this.startRetryTimer() // Reschedule for next retry
            return
        }

        logger.info(`[SyncRetry] Processing ${dueRecords.length} due retries`)

        // Trigger sync for due records
        // This will be handled by the sync store
        const event = new CustomEvent('sync-retry-due', {
            detail: { recordIds: dueRecords }
        })
        window.dispatchEvent(event)

        // Remove processed records from queue (they'll be re-added if they fail again)
        for (const recordId of dueRecords) {
            this.retryQueue.delete(recordId)
        }

        // Reschedule timer for remaining items
        if (this.retryQueue.size > 0) {
            this.startRetryTimer()
        } else {
            this.retryTimer = null
        }
    }

    /**
     * Get retry queue size
     */
    getQueueSize(): number {
        return this.retryQueue.size
    }

    /**
     * Clear all retries (for testing)
     */
    clearQueue(): void {
        this.retryQueue.clear()
        if (this.retryTimer !== null) {
            clearTimeout(this.retryTimer)
            this.retryTimer = null
        }
        logger.info('[SyncRetry] Retry queue cleared')
    }

    /**
     * Get retry info for a specific record
     */
    getRetryInfo(recordId: string): RetryQueueItem | null {
        return this.retryQueue.get(recordId) || null
    }
}

// Export singleton instance
export const syncRetryService = new SyncRetryService()
export default syncRetryService
